import React from "react"
import {Box, Button, Dialog, DialogContent, DialogTitle, Typography, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox} from "@mui/material";

import {t, Trans} from "@lingui/macro";
import {ErrorDialog, MatesTextField, withUseFormHook} from "@sinossi/mates-react-library";
import {UseFormReturn} from "react-hook-form";
import * as Yup from "yup";
import {CustomDropzoneUploadDocument} from "../../../brand/customDropzoneUploadDocument";
import {DocumentType} from "../../../../models/document";

interface UploadDocumentModalState {
    errorDialog: {
        open: boolean
        title: string
        message: string
    }
    selectedDocumentType: DocumentType
    isPublic: boolean
}

interface UploadDocumentModalProps extends UseFormReturn{
    open: boolean
    onClose: () => void
    callbackUploadDocument: (formData: FormData) => void
    documentType?: string
    content?: string
    textfieldName?: string
    priority?: boolean
    isFromDocumentsPage?: boolean
}

const UploadDocumentModalSchema = Yup.object().shape({
    uploadDocumentNumber: Yup.string(),
})

class UploadDocumentModal extends React.Component<UploadDocumentModalProps, UploadDocumentModalState>{

    static defaultProps = {
        documentType: t({id: "upload-document-modal.generic-document", message: "Caricamento documento"}),
        content: t({id: "upload-document-modal.generic-content", message: "Trascina o clicca per caricare un documento"}),
        textfieldName: t({id: "upload-document-modal.textfield-name", message: "Numero documento"}),
        priority: false
    }

    private documentData: File | null = null

    private fileError: boolean = false

    constructor(props: any) {
        super(props)
        this.state = {
            errorDialog: {
                open: false,
                title: "",
                message: "",
            },
            selectedDocumentType: DocumentType.ATTACHMENT,
            isPublic: false
        }
    }

    private uploadDocument = (document: File) => {
        if (document){
            this.documentData = document
        }
    }

    private handleDocumentTypeChange = (event: any) => {
        this.setState({
            selectedDocumentType: event.target.value as DocumentType
        })
    }

    private handlePublicChange = (event: any) => {
        this.setState({
            isPublic: event.target.checked
        })
    }

    private submitDocument = () => {
        const request = this.props.isFromDocumentsPage ? {
            documentType: this.state.selectedDocumentType,
            documentNumber: this.props.getValues("uploadDocumentNumber")??"",
            isPublic: this.state.isPublic
        } : {
            documentType: "ATTACHMENT",
            documentNumber: this.props.getValues("uploadDocumentNumber")??"",
            isPublic: this.state.isPublic
        }

        const formData: FormData = new FormData()
        formData.append(
            "data",
            new Blob([ JSON.stringify(request) ], { type: "application/json" }),
            ""
        )
        if(this.documentData) {
            this.fileError = false
            formData.append("file", this.documentData)
            this.props.callbackUploadDocument(formData as FormData)
        } else {
            this.fileError = true
        }
    }

    private onErrorDialogClose = (): void => {
        this.setState(() => ({
            errorDialog: {
                open: false,
                title: "",
                message: "",
                okText: "",
            }
        }))
    }

    render () {
        return(
            <Dialog
                open={true}
                onClose={() => this.props.onClose()}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    {this.props.documentType}
                </DialogTitle>
                <form onSubmit={this.props.handleSubmit(this.submitDocument)}>
                    <DialogContent sx={{
                        p:2
                    }}>
                        <CustomDropzoneUploadDocument callback={this.uploadDocument} text={this.props.content!}/>
                        {this.fileError &&
                            <Typography sx={{color: "red", ml: 2, fontSize: "12px"}}>
                                <Trans id="documents-table.required">
                                    Il campo è obbligatorio
                                </Trans>
                            </Typography>
                        }
                        {this.props.isFromDocumentsPage && (
                            <Box sx={{mt: 3}}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>
                                        <Trans id="upload-document-modal.document-type">
                                            Tipo documento
                                        </Trans>
                                    </InputLabel>
                                    <Select
                                        value={this.state.selectedDocumentType}
                                        onChange={this.handleDocumentTypeChange}
                                        label="Tipo documento"
                                    >
                                        <MenuItem value={DocumentType.PHOTO}>
                                            <Trans id="document-type.photo">Foto</Trans>
                                        </MenuItem>
                                        <MenuItem value={DocumentType.PLANIMETRY}>
                                            <Trans id="document-type.planimetry">Planimetria</Trans>
                                        </MenuItem>
                                        <MenuItem value={DocumentType.ATTACHMENT}>
                                            <Trans id="document-type.attachment">Allegato</Trans>
                                        </MenuItem>
                                        <MenuItem value={DocumentType.TOUR_3D}>
                                            <Trans id="document-type.tour-3d">Tour 3D</Trans>
                                        </MenuItem>
                                        <MenuItem value={DocumentType.OTHER}>
                                            <Trans id="document-type.other">Altro</Trans>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                        <Box sx={{mt: 2}}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={this.state.isPublic}
                                        onChange={this.handlePublicChange}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Trans id="upload-document-modal.is-public">
                                        Documento pubblico
                                    </Trans>
                                }
                            />
                        </Box>
                        {
                            !this.props.priority &&
                            <Box sx={{mt: 3}}>
                                <MatesTextField name="uploadDocumentNumber"
                                                label={this.props.textfieldName!}
                                                variant="outlined"
                                                control={this.props.control}
                                                trigger={this.props.trigger}
                                                formState={this.props.formState} />
                            </Box>
                        }
                    </DialogContent>
                    <Box display="flex" justifyContent="center" sx={{mt: 2, mb: 3}}>
                        <Button variant="contained"
                                type="submit"
                                disableElevation
                                sx={{
                                    textTransform: 'none',
                                    backgroundColor: 'secondary.main',
                                    color: 'white',
                                    paddingX: 5,
                                    height: 28,
                                }}
                                onClick={this.submitDocument}
                        >
                            <Trans id="documents-table.upload-button">
                                <Typography>
                                    Carica
                                </Typography>
                            </Trans>
                        </Button>
                    </Box>
                </form>
                <ErrorDialog
                    ok={this.onErrorDialogClose}
                    {...this.state.errorDialog}/>
            </Dialog>
        )
    }
}

export default withUseFormHook<{}>(
    UploadDocumentModal,
    UploadDocumentModalSchema
)
